import type { AsyncDataExecuteOptions } from 'nuxt';
import useNewFetchHeaders from './useNewFetchHeaders';

type ApiFetchOptions = {
    watch?: any;
    immediate?: boolean;
    lazy?: boolean;
    internal?: boolean;
    transformData?: boolean;
    requiresAuth?: boolean;
    baseURL?: string;
    headers?: Record<string, string>;
    query?: MaybeRef<Record<string, any>>;
    body?: MaybeRef<Record<string, any>>;
    onResponse?: (response: any) => any;
};

const getDefaultHeaders = (runtimeConfig: any): Record<string, string> => {
    return {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Context-Platform-Public-Auth-Token':
            runtimeConfig.platform.authToken || '',
    };
};

/**
 * Returns options with default values merged in
 *
 * @param {ApiFetchOptions} options
 * @param {*} runtimeConfig
 * @return {*}  {ApiFetchOptions}
 */
const getOptionsWithDefaults = (
    options: ApiFetchOptions,
    runtimeConfig: any,
): ApiFetchOptions => {
    const defaultOptions: ApiFetchOptions = {
        lazy: false,
        immediate: false,
        baseURL: runtimeConfig.appHost,
        transformData: false,
        internal: false,
        // Default require the auth header to be set
        requiresAuth: true,
        query: options.query,
        body: options.body,
    };

    return {
        ...defaultOptions,
        ...options,
    };
};

const logRequestContext = (
    requestContext: string,
    oldRequestContext?: string,
) => {
    const oldContext = (oldRequestContext ?? '').split(':');
    const newContext = requestContext.split(':');

    console.table(
        {
            new: {
                team: newContext[0] ?? '',
                product: newContext[1] ?? '',
                key: newContext[2] ?? '',
            },
            old: {
                team: oldContext[0] ?? '',
                product: oldContext[1] ?? '',
                key: oldContext[2] ?? '',
            },
        },
        ['team', 'product', 'key'],
    );
};

/**
 * Wrapper for nuxt internal useFetch composeable
 * with opiniated default we migth always use
 *
 * @param {string|Ref<string>} url
 * @param {string|Ref<string>} key
 * @param {Object} options
 * @returns {Promise<AsyncData<DataT>>|AsyncData<DataT>}
 */
export default function useNewApiFetch<T = any>(
    url: MaybeRef<string>,
    key: MaybeRef<string> = '',
    options: ApiFetchOptions = {},
) {
    const { public: runtimeConfig } = useRuntimeConfig();

    const optionsWithDefaults = getOptionsWithDefaults(options, runtimeConfig);

    // Headers
    const { requestContext, reqHeaders } = useNewFetchHeaders();

    // compute url we want to fetch
    const fetchUrl = computed(() => {
        let _url = unref(url);

        if (_url.startsWith('http')) {
            return _url;
        }

        if (!_url.startsWith('/')) {
            _url = `/${_url}`;
        }

        // if url is internal, we don't prepend baseURL
        if (optionsWithDefaults.internal) {
            return _url;
        }

        const baseURL = optionsWithDefaults.baseURL ?? '';

        return `${baseURL}${_url}`;
    });

    // compute headers we want to use
    const { headers: optHeaders } = optionsWithDefaults;

    const headers = computed(() => {
        return {
            ...getDefaultHeaders(runtimeConfig),
            ...reqHeaders.value,
            ...optHeaders,
        };
    });

    const {
        transformData: _,
        headers: __,
        ...fetchOptions
    } = optionsWithDefaults;

    // Request context is a combination of team and product headers
    // Plus the key if provided
    const reqKey = computed(() => {
        let keyValue = isRef(key) ? key.value : key;

        if (!keyValue) {
            keyValue = unref(url);
        }

        return `${requestContext.value}:${keyValue}`;
    });

    // Monkey patched useFetch to include our custom logic
    const fetch = useFetch<T>(fetchUrl, {
        key: reqKey,
        headers,
        immediate: false,
        watch: false,
        transform: (response: any) => {
            if (optionsWithDefaults.transformData) {
                return response.data || response;
            }

            return response;
        },
        ...fetchOptions,
    });

    // Override the execute method to add additional checks or logic
    const originalExecute = fetch.execute;
    const originalRefresh = fetch.refresh;

    /**
     * Custom execute method that includes additional checks
     */
    const executeWithChecks = async (
        opts?: AsyncDataExecuteOptions,
    ): Promise<void> => {
        if (optionsWithDefaults.requiresAuth && !headers.value.Authorization) {
            return;
        }

        return await originalExecute.call(fetch, opts);
    };

    /**
     * Custom refresh method that includes additional checks
     */
    const refreshWithChecks = async (
        opts?: AsyncDataExecuteOptions,
    ): Promise<void> => {
        if (optionsWithDefaults.requiresAuth && !headers.value.Authorization) {
            return;
        }

        return await originalRefresh.call(fetch, opts);
    };

    // Replace the execute method with our custom one
    fetch.execute = executeWithChecks;
    fetch.refresh = refreshWithChecks;

    // Check if we should execute immediately
    const immediate = options.immediate ?? true;

    if (immediate && !optionsWithDefaults.immediate) {
        // Automatically execute the fetch if immediate is true
        fetch.execute();
    }

    // TODO make this optional
    const debug = false;

    const watchSources = [
        reqKey,
        headers,
        () => toValue(optionsWithDefaults.query),
        () => toValue(optionsWithDefaults.body),
    ];

    // Watch for changes in the key and headers, and execute fetch if immediate is true
    watch(
        watchSources,
        async ([newKey, newhead, newQuery], [oldKey, oldhead, oldQuery]) => {
            if (debug) {
                logRequestContext(newKey, oldKey);
            }

            if (immediate) {
                await fetch.execute();
            }
        },
        {
            flush: 'sync',
            deep: true,
        },
    );

    return fetch;
}
